import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LivestreamPopout } from "../components/home/LivestreamPopout";
import { useIsMobileViewport } from "../hooks/useIsMobileViewport";
import { useLocalization } from "../hooks/useLocalization";
import { parseTwitchPlayerEmbedChannel } from "../services/twitchServices";

interface LivestreamPopoutState {
  embedSrc: string;
  chatSrc?: string;
  title: string;
}

interface LivestreamPopoutContextValue {
  isPoppedOut: boolean;
  popoutSupported: boolean;
  openPopout: (state: LivestreamPopoutState) => void;
  openPopouts: (states: LivestreamPopoutState[]) => void;
  closePopout: () => void;
  syncPopout: (state: LivestreamPopoutState) => void;
}

const LivestreamPopoutContext = createContext<LivestreamPopoutContextValue | null>(null);

export const LivestreamPopoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [popouts, setPopouts] = useState<LivestreamPopoutState[]>([]);
  const isMobile = useIsMobileViewport();
  const { strings } = useLocalization();
  const popoutSupported = !isMobile;

  const closePopout = useCallback(() => {
    setPopouts([]);
  }, []);

  const openPopout = useCallback(
    (state: LivestreamPopoutState) => {
      if (!popoutSupported) {
        return;
      }
      setPopouts([state]);
    },
    [popoutSupported],
  );

  const openPopouts = useCallback(
    (states: LivestreamPopoutState[]) => {
      if (!popoutSupported || states.length === 0) {
        return;
      }
      setPopouts(states.slice(0, 2));
    },
    [popoutSupported],
  );

  const syncPopout = useCallback((state: LivestreamPopoutState) => {
    setPopouts((current) => {
      if (current.length === 0) {
        return current;
      }
      if (current.length > 1) {
        return current;
      }

      const currentPrimary = current[0];
      const currentChannel = parseTwitchPlayerEmbedChannel(currentPrimary.embedSrc);
      const nextChannel = parseTwitchPlayerEmbedChannel(state.embedSrc);
      const channelChanged = nextChannel !== "" && currentChannel !== nextChannel;

      if (!channelChanged) {
        if (currentPrimary.title === state.title) {
          return current;
        }
        return [
          {
            embedSrc: currentPrimary.embedSrc,
            chatSrc: currentPrimary.chatSrc,
            title: state.title,
          },
          ...current.slice(1),
        ];
      }

      if (currentPrimary.embedSrc === state.embedSrc && currentPrimary.title === state.title) {
        return current;
      }

      return [state, ...current.slice(1)];
    });
  }, []);

  useEffect(() => {
    if (!popoutSupported && popouts.length > 0) {
      closePopout();
    }
  }, [popoutSupported, popouts, closePopout]);

  useEffect(() => {
    if (popouts.length === 0) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePopout();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [popouts, closePopout]);

  const value = useMemo(
    () => ({
      isPoppedOut: popouts.length > 0,
      openPopouts,
      popoutSupported,
      openPopout,
      closePopout,
      syncPopout,
    }),
    [popouts, popoutSupported, openPopout, openPopouts, closePopout, syncPopout],
  );

  return (
    <LivestreamPopoutContext.Provider value={value}>
      {children}
      {popouts.length > 0 && popoutSupported
        ? popouts.map((popout, index) => (
            <LivestreamPopout
              key={`${popout.embedSrc}-${String(index)}`}
              embedSrc={popout.embedSrc}
              chatSrc={popout.chatSrc}
              title={popout.title}
              dockLabel={strings.LIVE.DOCK}
              showChatLabel={strings.LIVE.SHOW_CHAT}
              hideChatLabel={strings.LIVE.HIDE_CHAT}
              onDock={closePopout}
              allowChat
              offsetIndex={index}
            />
          ))
        : null}
    </LivestreamPopoutContext.Provider>
  );
};

export const useLivestreamPopout = () => {
  const context = useContext(LivestreamPopoutContext);
  if (!context) {
    throw new Error("useLivestreamPopout must be used within LivestreamPopoutProvider");
  }
  return context;
};
